import {
    Button, ButtonVariant, ClickEvent, Component, Cursor, DataSource, DataSourceMode, Image, Label, Layout, List,
    Overlay, Panel, SelectionEvent, Snackbar, Tab, Tabset, Textbox
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { BrokeragePlanning } from "@mcleod/powerbroker/src/BrokeragePlanning";
import { CustomerService } from "./csm/CustomerService";
import {
    Alignment, ArrayUtil, DOMUtil, HorizontalAlignment, ModelRow, ObjectUtil, UrlUtil, WindowTitle
} from "@mcleod/core";
import { DataLoadActionHandler, UserLayoutProvider } from "@mcleod/common";
import { TextboxVariant } from "@mcleod/components/src/components/textbox/TextboxVariant";
import { CustomerServiceProfileFilter } from "./csm/CustomerServiceProfileFilter";
import { BrokeragePlanningProfileFilter } from "@mcleod/powerbroker/src/BrokeragePlanningProfileFilter";
import { OrderPlanningProfileFilter } from "./OrderPlanningProfileFilter";
import { DriverManagement } from "./drivermgmt/DriverManagement";
import { DriverManagerProfileFilter } from "./drivermgmt/DriverManagerProfileFilter";
import { OrderPlanningSplitView } from "./opsmgmt/OrderPlanningSplitView";
import { OrderPayablesProfileFilter } from "@mcleod/settlement/src/OrderPayablesProfileFilter";
import { OrderPayables } from "@mcleod/settlement";
import { DriverSettlement } from "@mcleod/settlement/src/driver-settlement/DriverSettlement";
import { DriverSettlementProfileFilter } from "@mcleod/settlement/src/driver-settlement/DriverSettlementProfileFilter";
import { CustomerRelationshipManager } from "@mcleod/crm";
import { OfficePayrollProcessing } from "@mcleod/backoffice/src/officepayroll/OfficePayrollProcessing";
import { OfficePayrollProfileFilter } from "@mcleod/backoffice/src/officepayroll/OfficePayrollProfileFilter";

class ProfileListItem extends Label {
    private _profileId: string;

    constructor(profileName: string, profileId: string) {
        super({ text: profileName });
        this.profileId = profileId;
    }

    get profileId(): string {
        return this._profileId;
    }

    set profileId(value: string) {
        this._profileId = value;
    }
}

type PrimaryLayout =
    BrokeragePlanning
    | OrderPlanningSplitView
    | CustomerService
    | DriverManagement
    | OrderPayables
    | DriverSettlement
    | CustomerRelationshipManager
    | OfficePayrollProcessing;
type ProfileFilterLayout =
    BrokeragePlanningProfileFilter
    | OrderPlanningProfileFilter
    | CustomerServiceProfileFilter
    | DriverManagerProfileFilter
    | OrderPayablesProfileFilter
    | DriverSettlementProfileFilter
    | OfficePayrollProfileFilter;

interface MultiProfileProps {
    tabset: Tabset;
    profileDataSource: DataSource;
    mainLayoutName: string;
    advancedFilterLayout: string;
    startingProfiles: string[];
    profileDescrFieldName: string;
    dataSourceToAddListener?: DataSource;
    userSettingsCallback?: () => void;
    refreshAfterTabChange?: boolean;
}

export class MultiProfilePlanningTabs {
    private tabset: Tabset;
    private profileDataSource: DataSource;
    private mainLayoutName: string;
    private advancedFilterLayout: string;
    private profileDescrFieldName: string;
    private _userProfiles: string[] = [];
    private noProfileSnackbar: Snackbar;
    private dataSourceToAddListener: DataSource;
    private refreshAfterTabChange: boolean = false;

    constructor(props: MultiProfileProps) {
        this.tabset = props.tabset
        this.profileDataSource = props.profileDataSource;
        this.mainLayoutName = props.mainLayoutName;
        this.advancedFilterLayout = props.advancedFilterLayout;
        this.profileDescrFieldName = props.profileDescrFieldName;
        this.tabset.remove(this.tabset.getTab(0)); // remove placeholder tab
        this.dataSourceToAddListener = props.dataSourceToAddListener;
        this.profileDataSource.search(null, null, this.advancedFilterLayout).then(async () => {
            this._userProfiles = props.startingProfiles.filter(profile => this.profileDataSource.data.find(profileA => profileA.get("id") === profile) != null);
            if (this._userProfiles.length === 0) {
                const panel = new Panel();
                const label = new Label({ text: "No profiles set. Please update them in your User Settings." });
                const button = new Button({
                    caption: "Open User Settings",
                    onClick: () => {
                        if (props.userSettingsCallback) {
                            props.userSettingsCallback();
                        } else {
                            UserLayoutProvider.navigateTo();
                        }
                        this.noProfileSnackbar.dismiss();
                    }
                });
                panel.add(label);
                panel.add(button);
                this.noProfileSnackbar = Snackbar.showWarningSnackbar(panel, { persist: true });
            }
            this.manageWindowTitle();

            this.tabset.addAfterTabSelectionListener((event: SelectionEvent) => this.doAfterTabSelection());
            await Promise.all(this._userProfiles.map(profile => this.addNewProfileTab(profile)));
            this.setupAddButton();
            if (this.dataSourceToAddListener != null) {
                const currentProfileRow = this.profileDataSource.data.find(profileA => profileA.get("id") === this._userProfiles[0])?.createBasicCopy();
                await this.dataSourceToAddListener?.search({profile_row: currentProfileRow});
                this.dataSourceToAddListener.clear();
            }
            if (this._userProfiles.length > 0) {
                this.tabset.selectedIndex = 0;
                this.handleDataLoadActions();
            }
            if (props.refreshAfterTabChange) {
                this.refreshAfterTabChange = props.refreshAfterTabChange;
            }
        });
    }

    handleDataLoadActions() {
        const userPrimaryProfile = this.tabset.getTab(0) as Tab;
        const primaryLayout = userPrimaryProfile.components[0] as PrimaryLayout;
        const props = UrlUtil.getPropsFromUrl(window.location.href);
        if (props != null) {
            const dataLoadActionProps = DataLoadActionHandler.gatherFromUrlProps(props);
            if (ArrayUtil.isEmptyArray(dataLoadActionProps) === false) {
                DataLoadActionHandler.handleDataLoadActions(primaryLayout, dataLoadActionProps, props);
            }
        }
    }

    setupAddButton() {
        const addButton = new Button({
            imageName: ImageName.add,
            variant: ButtonVariant.round,
            color: "primary",
            imageHeight: 22,
            imageWidth: 22,
            margin: "auto",
            cursor: Cursor.POINTER
        });
        addButton.addClickListener(() => {
            this.showProfileOverlay(addButton);
        });
        this.tabset.addAfterTool(addButton);
    }

    async addNewProfileTab(profile: string) {
        const tab = new Tab({ id: "id" + profile, paddingTop: 0 });
        const mainLayout = Layout.getLayout(this.mainLayoutName) as PrimaryLayout;
        tab.add(mainLayout);
        this.tabset.add(tab);
        tab.fillHeight = true;
        if (!this._userProfiles.find(profileA => profileA == profile)) {
            this._userProfiles.push(profile);
        }
        await mainLayout.ensureLoaded();
        this.updateProfile(profile, mainLayout, tab);
        if (this.refreshAfterTabChange){
            this.refreshAfterTabChange = false;
            this.tabset.selectedIndex = tab._index;
            this.refreshAfterTabChange = true;
        } else {
            this.tabset.selectedIndex = tab._index;
        }

    }

    async updateProfile(profile: string, layout: PrimaryLayout, tab: Tab) {
        await layout.loadProfile(profile);
        WindowTitle.refresh("MultiProfilePlanning (Profile Loaded");
        tab.heading.removeAll();
        tab.heading.paddingLeft = 12; // by default the tab heading has an uneven paddingLeft of 16px and paddingRight of 12px
        const currentProfileRow = this.profileDataSource.data.find(profileA => profileA.get("id") === profile)?.createBasicCopy();
        const profileName = currentProfileRow?.get(this.profileDescrFieldName, currentProfileRow?.get("id"));
        const quickInfoLayout = currentProfileRow?.getMetadata()?.quickInfoLayout;
        const profileLabel = new Label({
            id: "tabProfileLabel",
            text: profileName.length > 2 ? profileName.toUpperCase().slice(0, 2) : profileName.toUpperCase(),
            borderRadius: 24,
            borderWidth: 0,
            fontSize: "large",
            padding: 8,
            width: 36,
            align: HorizontalAlignment.CENTER,
            backgroundColor: currentProfileRow.get("profile_color", "primary"),
            color: "primary.reverse",
            tooltipPosition: Alignment.RIGHT
        });
        if (quickInfoLayout != null) {
            profileLabel.quickInfoLayout = quickInfoLayout;
            profileLabel.quickInfoOverrideKeyValue = profile;
        } else {
            profileLabel.tooltip = profileName;
        }
        profileLabel.addClickListener((event: ClickEvent) => {
            if (tab.selected) {
                this.showProfileOverlay(profileLabel, currentProfileRow, layout, tab);
            } else {
                this.tabset.selectedIndex = tab._index;
            }
        });
        profileLabel._element.addEventListener("contextmenu", (event: MouseEvent) => {
            event.preventDefault();
            tab.heading.dismissAllPopups();
            this.showProfileOverlay(profileLabel, currentProfileRow, layout, tab);
        });
        tab.heading.addMouseEnterListener(() => {
            profileLabel.style.opacity = "1";
        });
        tab.heading.addMouseLeaveListener(() => {
            profileLabel.style.opacity = this.tabset.selectedIndex === tab._index ? "1" : "0.5";
        });
        tab.heading.add(profileLabel);
        this.tabset.refreshUnderline();
    }

    showProfileOverlay(anchorComponent: Component, currentProfileRow?: ModelRow, layout?: PrimaryLayout, tab?: Tab) {
        const mainPanel = this.buildOverlayPanel(anchorComponent, currentProfileRow, layout, tab);
        const anchorBounds = anchorComponent._element.getBoundingClientRect();
        Overlay.showInOverlay(mainPanel, {
            style: {
                position: "absolute",
                top: DOMUtil.getSizeSpecifier(anchorBounds.top),
                left: DOMUtil.getSizeSpecifier(anchorBounds.right + 11)
            }
        });
        const filterTextbox = mainPanel.findComponentById("filterTextbox") as Textbox;
        filterTextbox.focus();
    }

    private buildOverlayPanel(anchorComponent: Component, currentProfileRow?: ModelRow, layout?: PrimaryLayout, tab?: Tab): Panel {
        const otherProfiles = this.profileDataSource.data.filter(layout => !this._userProfiles.includes(layout.get("id")));
        const mainPanel = new Panel({
            padding: 0,
            backgroundColor: "primary.reverse",
            width: 250,
            wrap: false,
            borderWidth: 1,
            borderColor: "subtle.lightest",
            borderRadius: 4,
            borderShadow: true
        });
        const currentProfile = currentProfileRow != null ? currentProfileRow.get("id") : null;
        if (currentProfileRow != null) {
            const tabCount = this.tabset.components.length;
            const labelTitle = new Label({
                text: currentProfileRow.get(this.profileDescrFieldName, currentProfileRow.get("id")),
                fontSize: "large",
                fontBold: true,
                color: "subtle.darkest",
                rowBreak: false
            });
            mainPanel.add(labelTitle);
            const filterIcon = this.generateFilterIcon(currentProfileRow, layout, anchorComponent, tab);
            mainPanel.add(filterIcon);
            if (tabCount > 1) {
                filterIcon.rowBreak = false;
                const deleteTabButton = new Button({
                    imageName: ImageName.circleX,
                    variant: ButtonVariant.round,
                    color: "subtle.darker",
                    imageHeight: 22,
                    imageWidth: 22,
                    cursor: Cursor.POINTER,
                    marginLeft: -6
                });
                deleteTabButton.addClickListener(() => {
                    this._userProfiles = this._userProfiles.filter(profile => profile !== currentProfile);
                    if (layout.mainDataSource != null) {
                        layout.mainDataSource.subscribeToChanges = false;
                    }
                    this.tabset.remove(tab);
                    const overlay = Overlay.findOverlay(mainPanel);
                    Overlay.hideOverlay(overlay);
                });
                deleteTabButton.addMouseEnterListener(() => {
                    deleteTabButton.color = "error";
                });
                deleteTabButton.addMouseLeaveListener(() => {
                    deleteTabButton.color = "subtle.darker";
                });
                mainPanel.add(deleteTabButton);
            }
        }
        const filterTextbox = new Textbox({
            id: "filterTextbox",
            placeholder: "Filter",
            captionVisible: false,
            fillRow: true,
            variant: TextboxVariant.UNDERLINED,
            imagePreName: ImageName.funnelFilled
        });
        filterTextbox.addChangeListener((event) => {
            list.filter(filterTextbox.text);
        })
        const list = new List({
            items: otherProfiles.map(layout => new ProfileListItem(layout.get(this.profileDescrFieldName, layout.get("id")), layout.get("id"))),
            height: 400,
            fillRow: true,
            color: "primary"
        });
        list.addSelectionListener((event: SelectionEvent) => {
            const newProfile = event.newSelection as ProfileListItem;
            this._userProfiles = this._userProfiles.filter(profile => profile !== currentProfile);
            this._userProfiles.push(newProfile.profileId);

            if (tab != null && layout != null) {
                this.updateProfile(newProfile.profileId, layout, tab);
            } else {
                // if there's no tab or layout, then this overlay was called from the add button, so we add a new profile tab
                this.addNewProfileTab(newProfile.profileId);
            }
            const overlay = Overlay.findOverlay(mainPanel);
            Overlay.hideOverlay(overlay);

            if (this.noProfileSnackbar != null) {
                this.noProfileSnackbar.dismiss();
                this.noProfileSnackbar = null;
            }
        });
        mainPanel.add(filterTextbox, list);
        return mainPanel;
    }

    generateFilterIcon(currentProfileRow: ModelRow, layout: PrimaryLayout, profileLabel: Component, tab: Tab): Button {
        const filterIcon = new Button({
            imageName: ImageName.funnel,
            rowBreak: true,
            variant: ButtonVariant.round,
            color: "subtle.darker",
            imageHeight: 23,
            imageWidth: 23,
            cursor: Cursor.POINTER,
            marginLeft: "auto"
        });
        filterIcon.addClickListener((event: ClickEvent) => {
            Layout.getLayout(this.advancedFilterLayout, {
                backgroundColor: "defaultBackground",
                borderShadow: true,
                paddingBottom: 50,
                fillHeight: true
            }).addLayoutLoadListener(event => {
                this.showFilter(event.target as ProfileFilterLayout, currentProfileRow, layout, (toggle: boolean) => {
                    let filterAppliedImage = tab.heading.findComponentById("filterAppliedImage") as Image;
                    if (toggle) {
                        if (filterAppliedImage != null) return;
                        filterAppliedImage = new Image({
                            id: "filterAppliedImage",
                            name: ImageName.funnelFilled,
                            width: 20,
                            height: 20,
                            color: profileLabel.backgroundColor
                        });
                        const profileLabelBounds = profileLabel._element.getBoundingClientRect();
                        filterAppliedImage.style.position = "absolute";
                        filterAppliedImage.style.left = DOMUtil.getSizeSpecifier(profileLabelBounds.width - 2);
                        filterAppliedImage.style.top = DOMUtil.getSizeSpecifier(profileLabelBounds.height - 5);
                        tab.heading.add(filterAppliedImage);
                    } else if (filterAppliedImage != null) {
                        tab.heading.remove(filterAppliedImage);
                    }
                });
            });
        });
        filterIcon.addMouseEnterListener(() => {
            filterIcon.color = "primary";
        });
        filterIcon.addMouseLeaveListener(() => {
            filterIcon.color = "subtle.darker";
        });
        return filterIcon;
    }

    showFilter(filterLayout: ProfileFilterLayout, profileRow: ModelRow, layout: PrimaryLayout, filterAppliedCallback: (toggle: boolean) => void) {
        const row = new ModelRow(filterLayout.mainDataSource.url);
        row.setValuesAndLookupModelData(profileRow);
        filterLayout.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, [row]);
        filterLayout.buttonCancel.addClickListener(() => Overlay.hideOverlay(overlay));
        filterLayout.buttonApply.addClickListener(async () => {
            if (filterLayout.validateSimple()) {
                Overlay.hideAllOverlays();
                const originalRow = this.profileDataSource.data.find(profile => profile.get("id") === profileRow.get("id"));
                profileRow.setValuesAndLookupModelData(row);
                await layout.loadProfile(row);
                if (!ObjectUtil.deepEqual(originalRow.data, row.data)) {
                    filterAppliedCallback(true);
                } else {
                    filterAppliedCallback(false);
                }
            }
        });
        filterLayout.buttonReset.addClickListener(async () => {
            Overlay.hideAllOverlays();
            const originalRow = this.profileDataSource.data.find(profile => profile.get("id") === profileRow.get("id"));
            Object.keys(profileRow.data).forEach(key => {
                profileRow.set(key, originalRow.get(key));
            });
            await layout.loadProfile(originalRow);
            filterAppliedCallback(false);
        });
        const panelColorSelector = filterLayout.findComponentById("panelColorSelector") as Panel;
        if (panelColorSelector != null) {
            panelColorSelector.visible = false;
        }
        const overlay = Overlay.showInOverlay(filterLayout, {
            closeOnClickOff: false,
            greyedBackground: true,
            centered: true
        });
    }

    private doAfterTabSelection() {
        this.toggleAllTabStyling();
        WindowTitle.refresh("MultiProfilePlanning (Tab Selected)");
        if (this.refreshAfterTabChange) {
            const selectedTab = this.tabset.getTab(this.tabset.selectedIndex) as Tab
            const primaryLayout = selectedTab.components[0] as PrimaryLayout;
            if (primaryLayout.mainDataSource?.lastSearch) {
                primaryLayout.mainDataSource.search(primaryLayout.mainDataSource.lastSearch);
            }
        }
    }

    private toggleAllTabStyling() {
        const selectedTab = this.tabset.getTab(this.tabset.selectedIndex);
        const currentProfileLabel = selectedTab.heading?.findComponentById("tabProfileLabel") as Label;
        if (currentProfileLabel != null) {
            currentProfileLabel.style.opacity = "1";
        }
        this.tabset.components.forEach((tab: Tab) => {
            if (selectedTab._index !== tab._index) {
                const profileLabel = tab.heading?.findComponentById("tabProfileLabel") as Label
                if (profileLabel != null) {
                    profileLabel.style.opacity = "0.5";
                }
            }
        });
    }

    get userProfiles() {
        return this._userProfiles;
    }

    get selectedTabLayout(): PrimaryLayout | null {
        const selectedTab = this.tabset.getTab(this.tabset.selectedIndex);
        if (selectedTab && selectedTab.components && selectedTab.components.length > 0) {
            return selectedTab.components[0] as PrimaryLayout;
        }
        return null;
    }

    private manageWindowTitle() {
        WindowTitle.resetWith((() => this.selectedTabLayout?.windowTitle || "MultiProfile Planning"), "MultiProfilePlanning");
    }
}
